// Client-centric panels for Standard Calyvra: Policies, Documents (with due dates)
// and Notes — all attached directly to the client (no case flow).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import {
  ShieldCheck, Plus, Pencil, Trash2, Loader2, FileText, Upload, Download,
  MessageSquare, Send, Paperclip, CalendarClock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const POLICY_STATUS_COLORS = {
  Active: 'bg-green-100 text-green-700',
  Pending: 'bg-amber-100 text-amber-700',
  Superseded: 'bg-slate-200 text-slate-600',
  Terminated: 'bg-red-100 text-red-700',
  Cancelled: 'bg-orange-100 text-orange-700',
};

const DOC_STATUS_COLORS = {
  Requested: 'bg-amber-100 text-amber-700',
  Received: 'bg-sky-100 text-sky-700',
  Resolved: 'bg-green-100 text-green-700',
};

const money = (v) => (v || v === 0
  ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—');
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyPolicy = {
  insurance_carrier: '', member_id: '', plan_name: '', product_type: '', market_type: '', state: '',
  effective_date: '', end_date: '', monthly_premium: '', aptc_amount: '',
  final_member_responsibility: '', policy_status: 'Active', notes: '', agent_id: '',
};

// ---------------- Policies ----------------
function ClientPolicies({ clientId, constants, canEdit }) {
  const [policies, setPolicies] = useState([]);
  const [canDelete, setCanDelete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPolicy);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => { portalApi.getAgents().then(setAgents).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await portalApi.getClientPolicies(clientId);
      setPolicies(r.policies || []);
      setCanDelete(r.can_delete || false);
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyPolicy); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      insurance_carrier: p.insurance_carrier || '', member_id: p.member_id || '', plan_name: p.plan_name || '',
      product_type: p.product_type || '', market_type: p.market_type || '', state: p.state || '',
      effective_date: p.effective_date || '', end_date: p.end_date || '',
      monthly_premium: p.monthly_premium ?? '', aptc_amount: p.aptc_amount ?? '',
      final_member_responsibility: p.final_member_responsibility ?? '',
      policy_status: p.policy_status || 'Active', notes: p.notes || '', agent_id: p.agent_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        product_type: form.product_type || null,
        market_type: form.market_type || null,
        state: form.state || null,
        monthly_premium: form.monthly_premium !== '' ? parseFloat(form.monthly_premium) : null,
        aptc_amount: form.aptc_amount !== '' ? parseFloat(form.aptc_amount) : null,
        final_member_responsibility: form.final_member_responsibility !== '' ? parseFloat(form.final_member_responsibility) : null,
        agent_id: form.agent_id || null,
      };
      if (editing) { await portalApi.updateClientPolicy(clientId, editing.id, payload); toast.success('Policy updated'); }
      else { await portalApi.createClientPolicy(clientId, payload); toast.success('Policy added'); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await portalApi.deleteClientPolicy(clientId, deleteTarget.id); toast.success('Policy deleted'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(err.message); setDeleteTarget(null); }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="client-policies">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-sky-500" />
          <h3 className="font-semibold text-slate-900">Policies</h3>
          <span className="text-sm text-slate-400">({policies.length})</span>
        </div>
        {canEdit && <Button size="sm" onClick={openCreate} data-testid="client-add-policy-btn"><Plus className="h-4 w-4 mr-1" /> Add Policy</Button>}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
        : policies.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="client-no-policies">No policies yet.</p>
        : (
          <div className="space-y-3">
            {policies.map((p) => (
              <div key={p.id} className="border border-slate-200 rounded-lg p-4" data-testid={`client-policy-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{p.insurance_carrier || 'Unnamed carrier'}</span>
                      <Badge className={POLICY_STATUS_COLORS[p.policy_status] || 'bg-slate-100 text-slate-700'}>{p.policy_status}</Badge>
                      {p.product_type && <Badge variant="outline" className="text-indigo-600 border-indigo-200">{p.product_type}</Badge>}
                      {p.state && <Badge variant="outline" className="text-slate-600">{p.state}</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{p.plan_name || '—'}{p.member_id ? ` · Member ${p.member_id}` : ''}</p>
                    {p.agent_name && <p className="text-xs text-sky-600 mt-0.5">Agent: {p.agent_name}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`client-edit-policy-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      {canDelete && <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`client-delete-policy-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                  <div><p className="text-slate-400 text-xs">Effective</p><p className="text-slate-700">{p.effective_date || '—'}</p></div>
                  <div><p className="text-slate-400 text-xs">End / Term</p><p className="text-slate-700">{p.end_date || '—'}</p></div>
                  <div><p className="text-slate-400 text-xs">Monthly</p><p className="text-slate-700">{money(p.monthly_premium)}</p></div>
                  <div><p className="text-slate-400 text-xs">Member Resp.</p><p className="text-slate-700">{money(p.final_member_responsibility)}</p></div>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-policy-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Policy' : 'Add Policy'}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Insurance Carrier</Label><Input value={form.insurance_carrier} onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} data-testid="client-policy-carrier" /></div>
            <div className="space-y-2"><Label>Member ID</Label><Input value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} /></div>
            <div className="space-y-2"><Label>Plan Name</Label><Input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Product Type</Label>
              <Select value={form.product_type || 'none'} onValueChange={(v) => setForm({ ...form, product_type: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="client-policy-product"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{(constants?.product_types || []).map(pt => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>State</Label>
              <Select value={form.state || 'none'} onValueChange={(v) => setForm({ ...form, state: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select state (optional)" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{(constants?.us_states || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Policy Status</Label>
              <Select value={form.policy_status} onValueChange={(v) => setForm({ ...form, policy_status: v })}>
                <SelectTrigger data-testid="client-policy-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(constants?.policy_statuses || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>End / Termination Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            {agents.length > 0 && (
              <div className="space-y-2"><Label>Agent / Producer</Label>
                <Select value={form.agent_id || 'none'} onValueChange={(v) => setForm({ ...form, agent_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="client-policy-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Unassigned —</SelectItem>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>Monthly Premium ($)</Label><Input type="number" value={form.monthly_premium} onChange={(e) => setForm({ ...form, monthly_premium: e.target.value })} /></div>
            <div className="space-y-2"><Label>APTC ($)</Label><Input type="number" value={form.aptc_amount} onChange={(e) => setForm({ ...form, aptc_amount: e.target.value })} /></div>
            <div className="space-y-2"><Label>Member Responsibility ($)</Label><Input type="number" value={form.final_member_responsibility} onChange={(e) => setForm({ ...form, final_member_responsibility: e.target.value })} /></div>
            <div className="space-y-2 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          {!editing && form.policy_status === 'Active' && (
            <p className="text-xs text-amber-600">Any existing Active policy of the same product type will be marked "Superseded" and preserved.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="client-save-policy-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Policy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy?</AlertDialogTitle>
            <AlertDialogDescription>This action is recorded in the audit log. Policy: <strong>{deleteTarget?.insurance_carrier}</strong>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------- Documents ----------------
const emptyDoc = { document_type: '', label: '', requested_date: todayISO(), due_date: '', status: 'Requested', notes: '' };

function ClientDocuments({ clientId, constants, canEdit, canDelete }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyDoc);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDocs(await portalApi.getClientDocuments(clientId)); }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.document_type) { toast.error('Please choose a document type'); return; }
    setSaving(true);
    try {
      await portalApi.createClientDocument(clientId, {
        document_type: form.document_type, label: form.label || null,
        requested_date: form.requested_date || null, due_date: form.due_date || null,
        status: form.status, notes: form.notes || null,
      });
      toast.success('Document request added'); setDialogOpen(false); setForm(emptyDoc); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const changeStatus = async (doc, status) => {
    try { await portalApi.updateClientDocument(clientId, doc.id, { status }); load(); }
    catch (err) { toast.error(err.message); }
  };

  const upload = async (doc, file) => {
    if (!file) return;
    setUploadingId(doc.id);
    try {
      await portalApi.uploadDocumentFile(doc.case_id, doc.id, file);
      if (doc.status === 'Requested') await portalApi.updateClientDocument(clientId, doc.id, { status: 'Received' });
      toast.success('File uploaded'); load();
    } catch (err) { toast.error(err.message); } finally { setUploadingId(null); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await portalApi.deleteClientDocument(clientId, deleteTarget.id); toast.success('Document deleted'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(err.message); setDeleteTarget(null); }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="client-documents">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-sky-500" />
          <h3 className="font-semibold text-slate-900">Documents</h3>
          <span className="text-sm text-slate-400">({docs.length})</span>
        </div>
        {canEdit && <Button size="sm" onClick={() => { setForm(emptyDoc); setDialogOpen(true); }} data-testid="client-add-document-btn"><Plus className="h-4 w-4 mr-1" /> Add Document</Button>}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
        : docs.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="client-no-documents">No documents requested.</p>
        : (
          <div className="space-y-3">
            {docs.map((d) => {
              const overdue = d.status === 'Requested' && d.due_date && d.due_date < todayISO();
              return (
                <div key={d.id} className="border border-slate-200 rounded-lg p-4" data-testid={`client-document-${d.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{d.document_type || d.category}</span>
                        <Badge className={DOC_STATUS_COLORS[d.status] || 'bg-slate-100 text-slate-700'}>{d.status}</Badge>
                        {overdue && <Badge className="bg-red-100 text-red-700" data-testid={`client-doc-overdue-${d.id}`}>Overdue</Badge>}
                      </div>
                      {d.label && <p className="text-sm text-slate-500 mt-1">{d.label}</p>}
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                        {d.requested_date && <span>Requested {d.requested_date}</span>}
                        {d.due_date && <span className={overdue ? 'text-red-600 font-medium flex items-center gap-1' : 'flex items-center gap-1'}><CalendarClock className="h-3 w-3" /> Due {d.due_date}</span>}
                      </p>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <Select value={d.status} onValueChange={(v) => changeStatus(d, v)}>
                          <SelectTrigger className="h-8 w-[130px]" data-testid={`client-doc-status-${d.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Requested">Requested</SelectItem>
                            <SelectItem value="Received">Received</SelectItem>
                            <SelectItem value="Resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" onChange={(e) => upload(d, e.target.files?.[0])} data-testid={`client-doc-upload-${d.id}`} />
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100">
                            {uploadingId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-slate-500" />}
                          </span>
                        </label>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(d)} data-testid={`client-doc-delete-${d.id}`} className={canDelete ? '' : 'hidden'}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    )}
                  </div>
                  {d.files?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {d.files.map((f) => (
                        <a key={f.id} href={portalApi.getDocumentFileUrl(d.case_id, d.id, f.id)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-sky-600 bg-sky-50 px-2 py-1 rounded" data-testid={`client-doc-file-${f.id}`}>
                          <Download className="h-3 w-3" /> {f.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-document-dialog">
          <DialogHeader><DialogTitle>Request Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Document Type</Label>
              <Select value={form.document_type || ''} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                <SelectTrigger data-testid="client-document-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{(constants?.document_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Label / Description (optional)</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Requested Date</Label><Input type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="client-document-due" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="client-save-document-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Request'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document request?</AlertDialogTitle>
            <AlertDialogDescription>Any uploaded files will be removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------- Notes ----------------
function ClientNotes({ clientId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setNotes(await portalApi.getClientNotes(clientId)); }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await portalApi.addClientNote(clientId, text.trim()); setText(''); load(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="client-notes">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-5 w-5 text-sky-500" />
        <h3 className="font-semibold text-slate-900">Notes</h3>
        <span className="text-sm text-slate-400">({notes.length})</span>
      </div>
      {canEdit && (
        <div className="flex items-start gap-2 mb-4">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="client-note-input" />
          <Button onClick={add} disabled={saving || !text.trim()} data-testid="client-add-note-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      )}
      {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
        : notes.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No notes yet.</p>
        : (
          <div className="space-y-3">
            {notes.map((n) => (
              <div key={n.id} className="border-l-2 border-slate-200 pl-3 py-1" data-testid={`client-note-${n.id}`}>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</p>
                <p className="text-xs text-slate-400 mt-1">{n.author_name} · {(n.created_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export function ClientWorkbench({ clientId, constants, canEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="space-y-6" data-testid="client-workbench">
      <ClientPolicies clientId={clientId} constants={constants} canEdit={canEdit} />
      <ClientDocuments clientId={clientId} constants={constants} canEdit={canEdit} canDelete={isAdmin} />
      <ClientNotes clientId={clientId} canEdit={canEdit} />
    </div>
  );
}

export default ClientWorkbench;
